import FormatCurrency from 'components/Common/FormatCurrency'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import CurrencyContext from 'contexts/currencyContext'
import useAutoCompactNavAccountOnMobile from 'hooks/Navigation/useAutoCompactNavAccountOnMobile'
import useInterval from 'hooks/useInterval'
import { useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import { memo, useCallback, useContext, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import NavBarButtonBase from '../NavBarButton/NavBarButtonBase'
import NavBarUserAvatar from '../NavBar/NavBarUserAvatar'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import AccountMenuDrawer from '../AccountMenu/AccountMenuDrawer'
import ModalContext from 'contexts/ModalContext'
import { closeLoyaltyRewardsNotification } from 'loyalty/components/LoyaltyRewardsNotification'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'

const FLIPPING_ROW_HEIGHT = rem(16)
const ACCOUNT_NAME_MAX_WIDTH = rem(144)
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'
const SUMMARY_ROTATION_INTERVAL_MS = 5000

type SummaryRowPosition = 'active' | 'above' | 'below'

function getPrefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false
  }

  return window.matchMedia(REDUCED_MOTION_QUERY).matches
}

function subscribeToReducedMotionPreference(
  mediaQueryList: MediaQueryList,
  listener: () => void,
): () => void {
  if (mediaQueryList.addEventListener) {
    mediaQueryList.addEventListener('change', listener)

    return () => mediaQueryList.removeEventListener('change', listener)
  }

  mediaQueryList.addListener(listener)

  return () => mediaQueryList.removeListener(listener)
}

const SummaryText = styled(BodyText)`
  display: block;
`

const FlippingContainer = styled.div`
  display: grid;
  grid-template-columns: max-content;
  grid-template-rows: 1fr;
  height: ${FLIPPING_ROW_HEIGHT};
  overflow: hidden;
`

const FlippingRow = styled(BodyText)<{ $position: SummaryRowPosition }>`
  grid-area: 1 / 1;
  display: block;
  opacity: ${props => props.$position === 'active' ? 1 : 0};
  transform: ${props => {
    if (props.$position === 'above') {
      return 'translateY(-100%)'
    }

    if (props.$position === 'below') {
      return 'translateY(100%)'
    }

    return 'translateY(0)'
  }};
  transition: opacity 300ms ease, transform 300ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const Button = styled(NavBarButtonBase)`
  position: relative;
  padding: ${rem(12)};
  padding-inline-start: ${rem(8)};
  display: flex;
  gap: ${rem(8)};
  align-items: center;
  text-align: start;
  transition: background-color 0.2s ease;

  ${mediaQueryDown.mobile} {
    padding: ${rem(12)};
  }

  ${mediaQueryUp.tablet} {
    width: max-content;
    max-width: none;
  }

  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    }
  }
`

const NameSection = styled.div<{ $compactOnMobile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;

  ${mediaQueryDown.mobile} {
    display: ${props => props.$compactOnMobile ? 'none' : 'flex'};
  }
`

const TruncatedNameLine = styled.div`
  max-width: ${ACCOUNT_NAME_MAX_WIDTH};
  min-width: 0;
  overflow: hidden;
`

const AccountSummaryMeasure = styled.div`
  position: fixed;
  top: 0;
  left: -10000px;
  visibility: hidden;
  pointer-events: none;
  width: max-content;
  height: auto;
  overflow: visible;
`

interface AccountSummaryProps {
  givenName: string | undefined
  showRotatingBalances: boolean
  hasPoints: boolean
  hasCredits: boolean
  showTierSummary: boolean
  loyaltyTier: App.LoyaltyTier | undefined
  loyaltyPoints: number
  creditBalance: number
  activeSummaryIndex: number
  accountFallbackLabel: string
  includeTestIds?: boolean
  forMeasurement?: boolean
}

function AccountSummary(props: AccountSummaryProps) {
  const {
    givenName,
    showRotatingBalances,
    hasPoints,
    hasCredits,
    showTierSummary,
    loyaltyTier,
    loyaltyPoints,
    creditBalance,
    activeSummaryIndex,
    accountFallbackLabel,
    includeTestIds = false,
    forMeasurement = false,
  } = props

  return <>
    <TruncatedNameLine>
      <BodyText variant="medium" wrap="truncate" colour="normal-contrast" data-measure-name>
        {givenName || accountFallbackLabel}
      </BodyText>
    </TruncatedNameLine>
    {showRotatingBalances && forMeasurement && (
      <>
        <SummaryText variant="small" wrap="no-wrap" colour="light-foreground-alt" data-measure-points>
          <FormatLoyaltyPoints abbreviated points={loyaltyPoints} />
        </SummaryText>
        <SummaryText variant="small" wrap="no-wrap" colour="light-foreground-alt" data-measure-credit>
          <FormatCurrency value={creditBalance} />
        </SummaryText>
      </>
    )}
    {showRotatingBalances && !forMeasurement && (
      <FlippingContainer data-testid={includeTestIds ? 'nav-account-summary' : undefined}>
        <FlippingRow
          variant="small"
          wrap="no-wrap"
          colour="light-foreground-alt"
          $position={activeSummaryIndex === 0 ? 'active' : 'above'}
          aria-hidden={activeSummaryIndex !== 0}
          data-testid={includeTestIds ? 'nav-account-points-summary' : undefined}
        >
          <FormatLoyaltyPoints abbreviated points={loyaltyPoints} />
        </FlippingRow>
        <FlippingRow
          variant="small"
          wrap="no-wrap"
          colour="light-foreground-alt"
          $position={activeSummaryIndex === 1 ? 'active' : 'below'}
          aria-hidden={activeSummaryIndex !== 1}
          data-testid={includeTestIds ? 'nav-account-credit-summary' : undefined}
        >
          <FormatCurrency value={creditBalance} />
        </FlippingRow>
      </FlippingContainer>
    )}

    {!showRotatingBalances && hasPoints && (
      <SummaryText variant="small" wrap="no-wrap" colour="light-foreground-alt">
        <FormatLoyaltyPoints abbreviated points={loyaltyPoints} />
      </SummaryText>
    )}

    {!showRotatingBalances && !hasPoints && hasCredits && (
      <SummaryText variant="small" wrap="no-wrap" colour="light-foreground-alt">
        <FormatCurrency value={creditBalance} />
      </SummaryText>
    )}

    {showTierSummary && loyaltyTier && (
      <BodyText variant="small" format="uppercase" weight="bold" wrap="truncate" colour={`tier-${loyaltyTier}-dark`}>
        {getLoyaltyTierDisplayName(loyaltyTier)}
      </BodyText>
    )}
  </>
}

interface Props {
  className?: string
}

function NavBarUserButton(props: Props) {
  const intl = useIntl()
  const account = useAppSelector(state => state.auth.account)
  const currency = useContext(CurrencyContext)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const loyaltyAccount = useAppSelector(state => state.loyalty.account.data)
  const [loyaltyTier] = useCurrentLoyaltyTier()
  const credits = useAppSelector(state => state.auth.account.creditsByCurrency[currency])
  const loyaltyPoints = loyaltyAccount?.points ?? 0
  const creditBalance = credits?.balance ?? 0
  const hasPoints = loyaltyPoints > 0
  const hasCredits = creditBalance > 0
  const showRotatingBalances = hasPoints && hasCredits
  const showTierSummary = !hasPoints && !hasCredits && hasLoyaltyAccount && !!loyaltyTier
  const [activeSummaryIndex, setActiveSummaryIndex] = useState(0)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(getPrefersReducedMotion)
  const measureRef = useRef<HTMLDivElement>(null)

  const accountFallbackLabel = intl.formatMessage({
    id: 'yruRQ-7AtFBKNQa-80cD',
    defaultMessage: 'Account',
    description: 'Fallback display name in navigation when user given name is not available',
  })

  const summaryProps: AccountSummaryProps = {
    givenName: account.givenName,
    showRotatingBalances,
    hasPoints,
    hasCredits,
    showTierSummary,
    loyaltyTier,
    loyaltyPoints,
    creditBalance,
    activeSummaryIndex,
    accountFallbackLabel,
  }

  const compactOnMobile = useAutoCompactNavAccountOnMobile(measureRef, [
    account.givenName,
    loyaltyPoints,
    creditBalance,
    showRotatingBalances,
    showTierSummary,
    loyaltyTier,
    currency,
  ])

  const showModal = useContext(ModalContext)
  const openAccountDrawer = useCallback(() => {
    closeLoyaltyRewardsNotification()
    showModal(<AccountMenuDrawer />)
  }, [showModal])
  const rotateSummary = useCallback(() => {
    setActiveSummaryIndex(currentIndex => currentIndex === 0 ? 1 : 0)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined
    }

    const mediaQueryList = window.matchMedia(REDUCED_MOTION_QUERY)
    const updateMotionPreference = () => setPrefersReducedMotion(mediaQueryList.matches)

    updateMotionPreference()

    return subscribeToReducedMotionPreference(mediaQueryList, updateMotionPreference)
  }, [])

  useEffect(() => {
    if (!showRotatingBalances || prefersReducedMotion) {
      setActiveSummaryIndex(0)
    }
  }, [prefersReducedMotion, showRotatingBalances])

  useInterval(rotateSummary, SUMMARY_ROTATION_INTERVAL_MS, {
    disabled: !showRotatingBalances || prefersReducedMotion,
  })

  return <Button
    onClick={openAccountDrawer}
    className={props.className}
    data-testid="nav-account"
    data-nav-account
  >
    <AccountSummaryMeasure ref={measureRef} aria-hidden>
      <AccountSummary {...summaryProps} forMeasurement />
    </AccountSummaryMeasure>
    <span data-nav-account-avatar>
      <NavBarUserAvatar />
    </span>
    <NameSection
      $compactOnMobile={compactOnMobile}
      data-testid="nav-account-labels"
      data-nav-account-labels
    >
      <AccountSummary {...summaryProps} includeTestIds />
    </NameSection>
  </Button>
}

export default memo(NavBarUserButton)
